import './fa.css';
import type React from 'react';
import type { Ref } from 'react';
import { useId } from 'react';

export interface IconDefinition {
  prefix: string;
  iconName: string;
  icon: [number, number, string[], string, string | string[]];
  fillRule?: React.SVGAttributes<SVGPathElement>['fillRule'];
  clipRule?: React.SVGAttributes<SVGPathElement>['clipRule'];
}

type IconGradientStop = { offset: string; color: string };
type IconGradient = {
  direction?: { x1: string; y1: string; x2: string; y2: string };
  stops: readonly IconGradientStop[];
};

export type FontAwesomeIconProps = {
  'aria-label'?: string;
  className?: string;
  color?: string;
  fontSize?: string | number;
  gradient?: IconGradient;
  height?: string | number;
  size?: string;
  title?: string;
  width?: string | number;
  icon: IconDefinition;
  ref?: Ref<SVGSVGElement>;
};

export const FontAwesomeIcon = ({
  ref,
  gradient,
  ...props
}: FontAwesomeIconProps) => {
  const id = useId();
  const gradientId = `${id}-gradient`;

  return (
    <svg
      ref={ref}
      aria-labelledby={props.title ? id : undefined}
      aria-hidden={props.title ? undefined : 'true'}
      aria-label={props['aria-label']}
      focusable={props.title ? undefined : 'false'}
      data-prefix={props.icon.prefix}
      data-icon={props.icon.iconName}
      className={[
        'svg-inline--fa',
        `fa-${props.icon.iconName}`,
        props.size ? `fa-${props.size}` : null,
        props.className ?? '',
      ]
        .filter(e => e != null)
        .join(' ')}
      role="img"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${props.icon.icon[0]} ${props.icon.icon[1]}`}
      width={props.width}
      height={props.height}
      fontSize={props.fontSize}
      color={gradient ? undefined : props.color}
    >
      {props.title ? <title id={id}>{props.title}</title> : null}
      {gradient ? (
        <defs>
          <linearGradient
            id={gradientId}
            x1={gradient.direction?.x1}
            y1={gradient.direction?.y1}
            x2={gradient.direction?.x2}
            y2={gradient.direction?.y2}
          >
            {gradient.stops.map(stop => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={stop.color}
              />
            ))}
          </linearGradient>
        </defs>
      ) : null}
      <path
        fill={gradient ? `url(#${gradientId})` : 'currentColor'}
        d={props.icon.icon[4] as string}
        fillRule={props.icon.fillRule}
        clipRule={props.icon.clipRule}
      />
    </svg>
  );
};
